import { useState, useEffect, useCallback, useMemo } from 'react';
import { shiftsAPI, staffAPI, v37API } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar, Plus, ChevronLeft, ChevronRight, Trash2, CalendarRange, Loader2 } from 'lucide-react';
import TouchTimePicker from '../components/TouchTimePicker';
import { cn } from '../lib/utils';

const getWeekStart = (d) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  date.setDate(date.getDate() + diff);
  return date.toISOString().split('T')[0];
};

const ShiftsPage = () => {
  const { hospital } = useAuth();
  const hospitalId = hospital?.id;
  const [shiftTypes, setShiftTypes] = useState([]);
  const [staff, setStaff] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [weekStart, setWeekStart] = useState(getWeekStart(new Date()));
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showTypeModal, setShowTypeModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);  // v37 — Bulk assign by pattern
  const [selectedSlot, setSelectedSlot] = useState({ staff_id: '', date: '' });

  const load = useCallback(async () => {
    try {
      const params = hospitalId ? { hospital_id: hospitalId } : {};
      const [r1, r2, r3] = await Promise.all([
        shiftsAPI.listTypes(params),
        staffAPI.list(),
        shiftsAPI.listAssignments({ ...params, week_start: weekStart }),
      ]);
      setShiftTypes(r1.data || []);
      setStaff((r2.data || []).filter(s => !hospitalId || s.hospital_id === hospitalId));
      setAssignments(r3.data || []);
    } catch { toast.error('Failed to load'); }
  }, [hospitalId, weekStart]);

  useEffect(() => { load(); }, [load]);

  const weekDates = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return { date: d.toISOString().split('T')[0], day: d.toLocaleDateString('en', { weekday: 'short' }), dayNum: d.getDate() };
  });

  const getAssignment = (staffId, date) => assignments.find(a => a.staff_id === staffId && a.date === date);

  const navigateWeek = (dir) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(d.toISOString().split('T')[0]);
  };

  return (
    <div className="space-y-5" data-testid="shifts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Shift Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">Weekly roster · {staff.length} staff · {shiftTypes.length} shift types</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowBulkModal(true)} data-testid="bulk-assign-btn"><CalendarRange size={14} className="mr-1" /> Bulk Assign</Button>
          <Button variant="outline" onClick={() => setShowTypeModal(true)} data-testid="add-shift-type-btn"><Plus size={14} className="mr-1" /> Shift Type</Button>
        </div>
      </div>

      {/* Shift Type Legend */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <p className="text-xs uppercase font-semibold text-slate-500 mb-2">Shift Types</p>
        <div className="flex flex-wrap gap-2">
          {shiftTypes.map(st => (
            <div key={st.id} className="flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ borderColor: `${st.color}40`, backgroundColor: `${st.color}10` }}>
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: st.color }} />
              <span className="text-sm font-medium" style={{ color: st.color }}>{st.name}</span>
              <span className="text-xs text-slate-500">{st.start_time}-{st.end_time}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Week navigator */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => navigateWeek(-1)} data-testid="prev-week-btn"><ChevronLeft size={14} /></Button>
        <div className="flex items-center gap-2 font-medium text-slate-700"><Calendar size={16} /> Week of {weekStart}</div>
        <Button variant="outline" size="sm" onClick={() => navigateWeek(1)} data-testid="next-week-btn"><ChevronRight size={14} /></Button>
      </div>

      {/* Roster Grid */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="text-left py-3 px-3 text-xs font-semibold text-slate-500 uppercase sticky left-0 bg-slate-50 z-10">Staff ({staff.length})</th>
              {weekDates.map(d => (
                <th key={d.date} className="text-center py-3 px-2 text-xs font-semibold text-slate-500 uppercase min-w-[100px]">
                  <div>{d.day}</div>
                  <div className="text-slate-400 font-normal">{d.dayNum}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {staff.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-400">No staff configured.</td></tr>
            ) : staff.slice(0, 100).map(s => (
              <tr key={s.id} className="border-b border-slate-50">
                <td className="py-2 px-3 sticky left-0 bg-white border-r border-slate-100">
                  <p className="font-medium text-slate-800 text-sm">{s.first_name} {s.last_name}</p>
                  <p className="text-xs text-slate-400">{s.department || s.role}</p>
                </td>
                {weekDates.map(d => {
                  const a = getAssignment(s.id, d.date);
                  const st = shiftTypes.find(t => t.id === a?.shift_type_id);
                  return (
                    <td key={d.date} className="py-1.5 px-1.5 text-center">
                      <button
                        onClick={() => {
                          setSelectedSlot({
                            staff_id: s.id, staff_name: `${s.first_name} ${s.last_name}`,
                            department: s.department, date: d.date,
                            // v37 — pass existing assignment so dialog opens in Edit mode
                            assignment: a || null,
                          });
                          setShowAssignModal(true);
                        }}
                        className="w-full h-9 rounded-lg border border-dashed border-slate-200 hover:border-teal-400 transition-all text-xs"
                        style={a && st ? { backgroundColor: `${st.color}20`, borderStyle: 'solid', borderColor: st.color, color: st.color } : {}}
                        data-testid={`shift-cell-${s.id}-${d.date}`}
                      >
                        {a ? (st?.name || a.shift_name) : '+'}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AssignModal open={showAssignModal} onClose={() => setShowAssignModal(false)} slot={selectedSlot} weekStart={weekStart} hospitalId={hospitalId} shiftTypes={shiftTypes} onSaved={load} />
      <ShiftTypeModal open={showTypeModal} onClose={() => setShowTypeModal(false)} hospitalId={hospitalId} onSaved={load} />
      <BulkAssignDialog open={showBulkModal} onClose={() => setShowBulkModal(false)} staff={staff} shiftTypes={shiftTypes} onSaved={load} />
    </div>
  );
};

// v37 — Now supports Edit + Delete (when slot.assignment is set)
const AssignModal = ({ open, onClose, slot, weekStart, hospitalId, shiftTypes, onSaved }) => {
  const isEdit = !!slot?.assignment;
  const [shiftTypeId, setShiftTypeId] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (open) setShiftTypeId(slot?.assignment?.shift_type_id || '');
  }, [open, slot]);

  const save = async () => {
    if (!shiftTypeId) { toast.error('Pick shift'); return; }
    setSaving(true);
    try {
      const st = shiftTypes.find(t => t.id === shiftTypeId);
      if (isEdit) {
        await v37API.updateShift(slot.assignment.id, {
          shift_type_id: shiftTypeId,
          shift_name: st?.name || '',
        });
        toast.success('Shift updated');
      } else {
        await shiftsAPI.createAssignment({
          hospital_id: hospitalId,
          staff_id: slot.staff_id,
          staff_name: slot.staff_name,
          department: slot.department || '',
          date: slot.date,
          week_start: weekStart,
          shift_type_id: shiftTypeId,
          shift_name: st?.name || '',
        });
        toast.success('Shift assigned');
      }
      onSaved(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!isEdit) return;
    if (!window.confirm(`Remove this shift for ${slot.staff_name} on ${slot.date}?`)) return;
    setDeleting(true);
    try {
      await v37API.deleteShift(slot.assignment.id);
      toast.success('Shift removed');
      onSaved(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setDeleting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>{isEdit ? 'Edit Shift' : 'Assign Shift'}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <p className="text-sm text-slate-600">{slot.staff_name} · {slot.date}</p>
          <div>
            <Label>Shift</Label>
            <Select value={shiftTypeId} onValueChange={setShiftTypeId}>
              <SelectTrigger data-testid="shift-type-select"><SelectValue placeholder="Select shift" /></SelectTrigger>
              <SelectContent>{shiftTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({t.start_time}-{t.end_time})</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex justify-between gap-2 pt-2">
            {isEdit ? (
              <Button variant="outline" onClick={remove} disabled={deleting || saving} className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1.5" data-testid="delete-shift-btn">
                {deleting ? <Loader2 className="animate-spin h-3 w-3" /> : <Trash2 size={12} />} Remove
              </Button>
            ) : <span />}
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-shift-btn">
                {saving ? '...' : (isEdit ? 'Save Changes' : 'Assign')}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// v37 — Bulk Assign by Pattern (multi-staff × date range × weekdays)
const WEEKDAY_LABELS = [
  { i: 0, label: 'Mon' }, { i: 1, label: 'Tue' }, { i: 2, label: 'Wed' },
  { i: 3, label: 'Thu' }, { i: 4, label: 'Fri' }, { i: 5, label: 'Sat' }, { i: 6, label: 'Sun' },
];

const BulkAssignDialog = ({ open, onClose, staff, shiftTypes, onSaved }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [shiftTypeId, setShiftTypeId] = useState('');
  const [staffIds, setStaffIds] = useState([]);
  const [weekdays, setWeekdays] = useState([0, 1, 2, 3, 4, 5]);  // Mon-Sat by default
  const [overwrite, setOverwrite] = useState(false);
  const [deptFilter, setDeptFilter] = useState('all');
  const [submitting, setSubmitting] = useState(false);

  const departments = useMemo(() => {
    const set = new Set();
    staff.forEach(s => s.department && set.add(s.department));
    return ['all', ...Array.from(set)];
  }, [staff]);

  const visibleStaff = useMemo(() => {
    if (deptFilter === 'all') return staff;
    return staff.filter(s => s.department === deptFilter);
  }, [staff, deptFilter]);

  useEffect(() => {
    if (!open) {
      setFrom(today); setTo(today); setShiftTypeId(''); setStaffIds([]); setOverwrite(false); setDeptFilter('all');
    }
    // eslint-disable-next-line
  }, [open]);

  const toggleWeekday = (i) => setWeekdays(w => w.includes(i) ? w.filter(x => x !== i) : [...w, i].sort());
  const toggleStaff = (id) => setStaffIds(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const selectAllVisible = () => setStaffIds(visibleStaff.map(s => s.id));
  const clearSelection = () => setStaffIds([]);

  const submit = async () => {
    if (!shiftTypeId) { toast.error('Pick a shift type'); return; }
    if (staffIds.length === 0) { toast.error('Select at least 1 staff'); return; }
    if (weekdays.length === 0) { toast.error('Pick at least 1 weekday'); return; }
    if (from > to) { toast.error('From date must be on or before To date'); return; }
    setSubmitting(true);
    try {
      const st = shiftTypes.find(t => t.id === shiftTypeId);
      const res = await v37API.bulkShiftPattern({
        staff_ids: staffIds,
        shift_type_id: shiftTypeId,
        shift_name: st?.name || '',
        from_date: from,
        to_date: to,
        weekdays,
        overwrite_existing: overwrite,
      });
      const { inserted, overwritten, skipped } = res.data;
      toast.success(`Assigned ${inserted}${overwritten ? ` · overwritten ${overwritten}` : ''}${skipped ? ` · skipped ${skipped}` : ''}`);
      onSaved(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><CalendarRange size={18} className="text-teal-600" /> Bulk Assign Shifts</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {/* Date Range */}
          <div className="grid grid-cols-2 gap-3">
            <div><Label>From Date *</Label><Input type="date" value={from} onChange={e => setFrom(e.target.value)} data-testid="bulk-from-date" /></div>
            <div><Label>To Date *</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} data-testid="bulk-to-date" /></div>
          </div>

          {/* Weekdays */}
          <div>
            <Label>Repeat on weekdays *</Label>
            <div className="flex gap-1.5 mt-1.5 flex-wrap">
              {WEEKDAY_LABELS.map(w => (
                <button
                  key={w.i}
                  type="button"
                  onClick={() => toggleWeekday(w.i)}
                  className={cn(
                    'px-3 py-1.5 text-xs rounded-md border transition-all font-medium',
                    weekdays.includes(w.i)
                      ? 'bg-teal-600 text-white border-teal-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'
                  )}
                  data-testid={`bulk-weekday-${w.i}`}>
                  {w.label}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-1">Shift will be assigned only on the selected weekdays within your date range.</p>
          </div>

          {/* Shift type */}
          <div>
            <Label>Shift Type *</Label>
            <Select value={shiftTypeId} onValueChange={setShiftTypeId}>
              <SelectTrigger data-testid="bulk-shift-type"><SelectValue placeholder="Select shift" /></SelectTrigger>
              <SelectContent>{shiftTypes.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({t.start_time}-{t.end_time})</SelectItem>)}</SelectContent>
            </Select>
          </div>

          {/* Staff picker */}
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label>Staff ({staffIds.length} selected)</Label>
              <div className="flex items-center gap-2">
                <Select value={deptFilter} onValueChange={setDeptFilter}>
                  <SelectTrigger className="h-7 text-xs w-44"><SelectValue /></SelectTrigger>
                  <SelectContent>{departments.map(d => <SelectItem key={d} value={d}>{d === 'all' ? 'All Departments' : d}</SelectItem>)}</SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={selectAllVisible} data-testid="bulk-select-all">Select All</Button>
                <Button variant="outline" size="sm" className="h-7 text-xs" onClick={clearSelection} data-testid="bulk-clear">Clear</Button>
              </div>
            </div>
            <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
              {visibleStaff.slice(0, 200).map(s => {
                const selected = staffIds.includes(s.id);
                return (
                  <label key={s.id} className={cn('flex items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-slate-50', selected && 'bg-teal-50')} data-testid={`bulk-staff-${s.id}`}>
                    <input type="checkbox" checked={selected} onChange={() => toggleStaff(s.id)} className="rounded" />
                    <span className="flex-1 truncate">{s.first_name} {s.last_name}</span>
                    <span className="text-slate-400">{s.department}</span>
                  </label>
                );
              })}
              {visibleStaff.length === 0 && <p className="text-center text-xs text-slate-400 py-4">No staff in this department.</p>}
            </div>
          </div>

          {/* Overwrite option */}
          <label className="flex items-center gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={overwrite} onChange={e => setOverwrite(e.target.checked)} className="rounded" data-testid="bulk-overwrite" />
            <span>Overwrite existing assignments on conflicting dates (otherwise they are skipped)</span>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={submitting} className="bg-teal-600 hover:bg-teal-700 gap-1.5" data-testid="bulk-submit">
            {submitting ? <Loader2 className="animate-spin h-3 w-3" /> : <CalendarRange size={14} />}
            {submitting ? 'Assigning…' : 'Assign Shifts'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ShiftTypeModal = ({ open, onClose, hospitalId, onSaved }) => {
  const [form, setForm] = useState({ name: '', start_time: '09:00', end_time: '17:00', color: '#10B981', allowance: 0 });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.name) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      await shiftsAPI.createType({ ...form, hospital_id: hospitalId, allowance: parseFloat(form.allowance) || 0 });
      toast.success('Created'); onSaved(); onClose();
      setForm({ name: '', start_time: '09:00', end_time: '17:00', color: '#10B981', allowance: 0 });
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>New Shift Type</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Late Night" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start</Label><TouchTimePicker value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} testId="shift-start" /></div>
            <div><Label>End</Label><TouchTimePicker value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} testId="shift-end" /></div>
            <div><Label>Color</Label><Input type="color" value={form.color} onChange={e => setForm({ ...form, color: e.target.value })} className="h-10 p-1" /></div>
            <div><Label>Allowance ₹</Label><Input type="number" value={form.allowance} onChange={e => setForm({ ...form, allowance: e.target.value })} /></div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-teal-600 hover:bg-teal-700" data-testid="save-shift-type-btn">{saving ? '...' : 'Create'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShiftsPage;
